import { Component, type ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ChildDetail } from '@/hooks/use-parent-data';
import { BadgesSection } from './badges-section';
import type { Id } from '../../../../convex/_generated/dataModel';
import { HOT_SHOTS_STAGES, type BallLevel } from '@/shared/hot-shots-stages';

class BadgesErrorBoundary extends Component<{ children: ReactNode }, { error: boolean }> {
  state = { error: false };
  static getDerivedStateFromError() { return { error: true }; }
  render() { return this.state.error ? null : this.props.children; }
}

interface OverviewTabProps {
  data: ChildDetail;
}


const SCORE_LABELS: Record<string, string> = {
  attitude: 'attitude',
  effort: 'effort',
  engagement: 'engagement',
  physical: 'fitness',
  technical: 'technique',
  tactical: 'tactics',
};

function buildSummaryText(eval_: OverviewTabProps['data']['evaluations'][0]): string {
  const scores: Record<string, number> = {
    attitude: eval_.attitude,
    effort: eval_.effort,
    engagement: eval_.engagement,
  };
  if (eval_.physical != null) scores.physical = eval_.physical;
  if (eval_.technical != null) scores.technical = eval_.technical;
  if (eval_.tactical != null) scores.tactical = eval_.tactical;

  const values = Object.values(scores);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const topKey = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
  const topLabel = SCORE_LABELS[topKey] ?? topKey;

  let headline: string;
  if (avg >= 3.5) headline = 'Excellent session overall';
  else if (avg >= 3.0) headline = 'Really solid session';
  else if (avg >= 2.5) headline = 'Good session with room to grow';
  else headline = 'Tough session — working on consistency';

  return `${headline}. Stood out for ${topLabel}.`;
}

export function OverviewTab({ data }: OverviewTabProps) {
  const ballLevel = data.profile.ballLevel as BallLevel | null;
  const stage = ballLevel && HOT_SHOTS_STAGES[ballLevel] ? HOT_SHOTS_STAGES[ballLevel] : null;
  const latestEval = data.evaluations[0] ?? null;
  const latestAttendance = data.attendance[0] ?? null;

  return (
    <div className="space-y-6">
      {/* Hot Shots Stage Card */}
      {stage && (
        <Card className={cn('border-l-4', stage.borderColor, stage.bgColor)}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl leading-none">{stage.emoji}</span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
                    Current Stage
                  </p>
                  <p className={cn('text-xl font-bold', stage.color)}>{stage.label}</p>
                  <p className="text-sm text-muted-foreground mt-1">{stage.description}</p>
                </div>
              </div>
            </div>
            {stage.next && (
              <div className="mt-3 pt-3 border-t border-border/30 flex items-center gap-2 text-sm text-muted-foreground">
                <div className={cn('w-2 h-2 rounded-full flex-shrink-0', HOT_SHOTS_STAGES[stage.next].dotColor)} />
                <span>Next stage: <strong className="text-foreground">{HOT_SHOTS_STAGES[stage.next].label}</strong></span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Last Session Summary */}
      {latestEval && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Last Session</p>
              {latestAttendance && (
                <span className="text-xs text-muted-foreground">
                  {new Date(latestAttendance.sessionDate).toLocaleDateString('en-AU', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}
                  {latestAttendance.sessionType && (
                    <> &middot; {latestAttendance.sessionType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</>
                  )}
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-foreground">{buildSummaryText(latestEval)}</p>
            {data.recentSessionNote && (
              <blockquote className="mt-3 pl-3 border-l-2 border-border text-sm text-muted-foreground italic">
                "{data.recentSessionNote.length > 200
                  ? data.recentSessionNote.slice(0, 200) + '…'
                  : data.recentSessionNote}"
                <span className="not-italic font-medium text-muted-foreground"> — Coach</span>
              </blockquote>
            )}
          </CardContent>
        </Card>
      )}

      {/* Badges */}
      <BadgesErrorBoundary>
        <BadgesSection childId={data.profile.id as Id<'players'>} />
      </BadgesErrorBoundary>

    </div>
  );
}
